
import React from 'react';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import LiveSessionChat from './LiveSessionChat';
import LiveSessionAudioMixer from './LiveSessionAudioMixer';
import SongwritingCollaboration from './SongwritingCollaboration';
import AdvancedSessionTools from './AdvancedSessionTools';
import { AudioRecording } from '@/hooks/useAudioRecorder';
import SidebarTabsList from './sidebar/SidebarTabsList';
import MobileSidebarHeader from './sidebar/MobileSidebarHeader';
import FilesTabContent from './sidebar/FilesTabContent';
import RecordingsTabContent from './sidebar/RecordingsTabContent';

interface LiveSessionSidebarProps {
  visible: boolean;
  onClose: () => void;
  isMobile: boolean;
  sessionFiles: any[];
  sessionName: string;
  isRecording: boolean;
  onToggleRecording: () => void;
  recordings: AudioRecording[];
  onDownloadRecording: (recording: AudioRecording, filename: string) => void;
  onClearRecordings: () => void;
  onDeleteRecording: (id: string) => void; // Include onDeleteRecording
  elapsedTime: number;
  session: any;
  audioLevel: number;
}

const LiveSessionSidebar: React.FC<LiveSessionSidebarProps> = ({
  visible,
  onClose,
  isMobile,
  sessionFiles,
  sessionName,
  isRecording,
  onToggleRecording,
  recordings,
  onDownloadRecording,
  onClearRecordings,
  onDeleteRecording, // Include in destructuring
  elapsedTime,
  session,
  audioLevel
}) => {
  if (!visible) return null;
  
  return (
    <div className="h-full flex flex-col w-full overflow-hidden bg-gradient-to-b from-[#1A1F2C] to-[#1A1F2C]/95 backdrop-blur-sm">
      {isMobile && <MobileSidebarHeader onClose={onClose} />}
    
      <Tabs defaultValue="chat" className="flex flex-col h-full overflow-hidden">
        <div className="flex-none">
          <SidebarTabsList />
        </div>
        
        <div className="flex-1 overflow-hidden mt-1">
          <TabsContent value="chat" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <ScrollArea className="h-full">
              <div className="p-4">
                <LiveSessionChat />
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="mixer" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <ScrollArea className="h-full p-4">
              <LiveSessionAudioMixer />
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="files" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <FilesTabContent sessionFiles={sessionFiles} />
          </TabsContent>
          
          <TabsContent value="lyrics" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <ScrollArea className="h-full">
              <div className="p-4">
                <SongwritingCollaboration sessionName={sessionName} />
              </div>
            </ScrollArea>
          </TabsContent>
          
          <TabsContent value="recordings" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <RecordingsTabContent
              isRecording={isRecording}
              onToggleRecording={onToggleRecording}
              recordings={recordings}
              onDownloadRecording={onDownloadRecording}
              onClearRecordings={onClearRecordings}
              onDeleteRecording={onDeleteRecording} // Pass to RecordingsTabContent
              elapsedTime={elapsedTime}
              audioLevel={audioLevel}
            />
          </TabsContent>
          
          <TabsContent value="ai" className="h-full m-0 p-0 data-[state=active]:flex data-[state=active]:flex-col">
            <AdvancedSessionTools 
              sessionId={session?.id || '1'}
              sessionName={sessionName}
              sessionDuration={session?.duration || 120}
              participantCount={session?.participants?.length || 6}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default LiveSessionSidebar;
